import { createContext, useContext, useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import {
    accountLabel,
    buildTieredAccountResolver,
    matchableAccount,
    withPersonLabels,
    type AccountResolver,
    type MatchableAccount,
    type UserDocType,
    type UserProfileDocType
} from '@ast-data/shared';
import { useStableQuery } from '../../db/useStableQuery';

/** Shows records' people by their accounts — see {@link UserDirectory}. */
export interface PersonLabels {
    /**
     * Whether accounts and profiles have arrived. Before that nothing is
     * replaced, and whatever freezes a record for editing has to wait for it.
     */
    ready: boolean;
    /** The account's current name, '' for an unknown id (or before `ready`). */
    labelOf(id: string): string;
    /** One record with its linked people shown by their accounts. */
    show<T extends object>(entityName: string, record: T): T;
    /** The same for a list; a record with nothing to change stays itself. */
    showAll<T extends object>(entityName: string, records: readonly T[]): T[];
}

/**
 * The app's accounts and their profiles, read ONCE for the whole app, and
 * everything worked out from them: the active ones, each account's name, the
 * name -> account rule, and the people shown on records.
 *
 * Before this, every caller of the user hooks held its own `users` and
 * `user_profiles` queries — a form with a few person fields a dozen of them,
 * each re-deriving its lists on every profile sync and each with its OWN idea
 * of whether the data had loaded. One form could know the profiles while the
 * name rule beside it did not yet; the records written in that window kept the
 * wrong name or lost their link.
 */
export interface UserDirectory {
    /** Every account, deactivated ones included — see useAllUsers. */
    users: UserDocType[];
    /** The active (non-disabled) accounts — see useActiveUsers. */
    activeUsers: UserDocType[];
    /** Every account, active ones first — see useUsersActiveFirst. */
    usersActiveFirst: UserDocType[];
    profiles: UserProfileDocType[];
    profileById: ReadonlyMap<string, UserProfileDocType>;
    /**
     * An account's display name: the app's custom name, else the Synology
     * username. Before `profilesReady` that is the login name for everybody —
     * see useUserLabelState for why nothing may STORE it then.
     */
    label: (user: UserDocType) => string;
    /** The profiles query has answered (a device may hold none at all). */
    profilesReady: boolean;
    /** Accounts AND profiles have answered: names and links are final. */
    ready: boolean;
    /** Every account as the name rule matches it (name + Crewmeister name). */
    accounts: MatchableAccount[];
    /** The name -> account rule: active accounts first, then all of them. */
    resolver: AccountResolver<MatchableAccount>;
    personLabels: PersonLabels;
}

/** The directory from the two live queries — the provider's work, once. */
export function useUserDirectoryData(): UserDirectory {
    const usersQuery = useStableQuery<UserDocType>({});
    const { results: usersDocs, loading: usersLoading } =
        useLiveRxQuery<UserDocType>({
            collection: 'users',
            query: usersQuery
        });
    const profilesQuery = useStableQuery<UserProfileDocType>({});
    const { results: profilesDocs, loading: profilesLoading } =
        useLiveRxQuery<UserProfileDocType>({
            collection: 'user_profiles',
            query: profilesQuery
        });

    const users = useMemo(
        () => usersDocs.map((doc) => doc.toMutableJSON() as UserDocType),
        [usersDocs]
    );
    const profiles = useMemo(
        () =>
            profilesDocs.map(
                (doc) => doc.toMutableJSON() as UserProfileDocType
            ),
        [profilesDocs]
    );
    const profilesReady = !profilesLoading;
    const ready = profilesReady && !usersLoading;

    return useMemo(() => {
        const profileById = new Map(
            profiles.map((profile) => [profile.id, profile])
        );
        const label = (user: UserDocType) =>
            accountLabel(user.username, profileById.get(user.id)?.customName);
        const activeUsers = users.filter((user) => !user.disabled);
        const accounts: MatchableAccount[] = users.map((user) =>
            matchableAccount(user, profileById.get(user.id))
        );
        const activeIds = new Set(activeUsers.map((user) => user.id));
        const resolver = buildTieredAccountResolver(
            accounts.filter((account) => activeIds.has(account.id)),
            accounts
        );
        // Nothing is replaced until both lists are in: before that a label is
        // the login name, and a form showing it would also SAVE it.
        const labelById = new Map<string, string>(
            ready ? users.map((user) => [user.id, label(user).trim()]) : []
        );
        const labelOf = (id: string) => labelById.get(id) ?? '';
        return {
            users,
            activeUsers,
            usersActiveFirst: [
                ...activeUsers,
                ...users.filter((user) => user.disabled)
            ],
            profiles,
            profileById,
            label,
            profilesReady,
            ready,
            accounts,
            resolver,
            personLabels: {
                ready,
                labelOf,
                show: (entityName, record) =>
                    withPersonLabels(entityName, record, labelOf),
                showAll: (entityName, records) =>
                    records.map((record) =>
                        withPersonLabels(entityName, record, labelOf)
                    )
            }
        };
    }, [users, profiles, profilesReady, ready]);
}

export const UserDirectoryContext = createContext<UserDirectory | null>(null);

/**
 * Without a provider (a bare component test with no database) there is nobody:
 * empty lists, names stay as stored, and nothing waits for data that will
 * never come.
 */
const NO_USERS: UserDirectory = (() => {
    const labelOf = () => '';
    return {
        users: [],
        activeUsers: [],
        usersActiveFirst: [],
        profiles: [],
        profileById: new Map(),
        label: (user: UserDocType) => user.username,
        profilesReady: true,
        ready: true,
        accounts: [],
        resolver: buildTieredAccountResolver([], []),
        personLabels: {
            ready: true,
            labelOf,
            show: (_entityName, record) => record,
            showAll: (_entityName, records) => [...records]
        }
    };
})();

/** The app's accounts, profiles and names — see {@link UserDirectory}. */
export function useUserDirectory(): UserDirectory {
    return useContext(UserDirectoryContext) ?? NO_USERS;
}
